import { useMergeRefs } from '@floating-ui/react';
import type { ReactElement, Ref } from 'react';
import { Children, cloneElement, useCallback, useRef, useState } from 'react';

import type { ReferenceProps, TooltipContentProps } from './TooltipContent';

import { TooltipContent } from './TooltipContent';

/**
 * Wrapper component that provides a simple API for adding tooltips to any element.
 * Uses cloneElement to attach refs to the child element.
 *
 * @example
 * ```tsx
 * <Tooltip content="Save your changes">
 *   <Button>Save</Button>
 * </Tooltip>
 * ```
 */
export interface TooltipProps extends Omit<TooltipContentProps, 'anchor' | 'children'> {
  /**
   * The element that triggers the tooltip. Must be a single ReactElement that accepts ref.
   */
  children: ReactElement;
  /**
   * The tooltip content to display
   */
  content: TooltipContentProps['children'];
}

interface ChildProps extends ReferenceProps {
  /**
   * Ref for the DOM element
   */
  ref?: Ref<HTMLElement>;
}

type GetReferenceProps = (userProps?: ReferenceProps) => ReferenceProps;

const Tooltip = ({ children, content, showOnHover = true, ...tooltipOptions }: TooltipProps) => {
  const anchorRef = useRef<HTMLElement>(null);

  const [getReferenceProps, setGetReferenceProps] = useState<GetReferenceProps | null>(null);

  const handleReferenceProps = useCallback((getProps: GetReferenceProps) => {
    setGetReferenceProps(() => getProps);
  }, []);

  const child = Children.only(children) as ReactElement<ChildProps>;

  const mergedRef = useMergeRefs([anchorRef, child.props.ref]);

  const hasContent =
    content != null && content !== false && content !== '' && !(Array.isArray(content) && content.length === 0);

  if (!hasContent) {
    return children;
  }

  /**
   * Get merged props using Floating UI's built-in event handler merging.
   * Pass the child's props to getReferenceProps so both tooltip handlers
   * and child's original handlers (onMouseLeave, etc.) work together.
   */
  const mergedProps = getReferenceProps ? getReferenceProps(child.props) : {};

  return (
    <>
      {cloneElement(child, { ref: mergedRef, ...mergedProps })}
      <TooltipContent
        anchor={anchorRef}
        onReferenceProps={handleReferenceProps}
        showOnHover={showOnHover}
        {...tooltipOptions}
      >
        {content}
      </TooltipContent>
    </>
  );
};

export default Tooltip;
